// src/components/Header/DynamicHeader.jsx
// FRONT OFFICE - Header with dynamic categories from database

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaUser, FaSignOutAlt, FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';
import './Header.css';
import logo from '../../images/logo.svg';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../services/AuthService';
import { getAllCategories } from '../../services/CategoryService';

const Header = () => {
  const { cartItems, cartTotals } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [showCategories, setShowCategories] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Safely calculate cart count and total
  const cartItemCount = cartItems ? cartItems.length : 0;
  const cartTotal = cartTotals ? cartTotals.total.toFixed(2) : '0.00';

  // Fetch categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setShowCategories(false);
  };

  const toggleCategories = () => {
    setShowCategories(!showCategories);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      closeMobileMenu();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-wrapper">
          <div className="logo">
            <Link to="/" onClick={closeMobileMenu}>
              <img
                src={logo}
                alt="PrimeCarePharmacy"
                className="logo-img"
              />
            </Link>
          </div>
          
          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}>
            <ul className="nav-list">
              <li 
                className={`nav-item home-item ${showCategories ? 'active' : ''}`}
              >
                <div 
                  className="nav-link with-dropdown"
                  onClick={toggleCategories}
                >
                  Home <FaChevronDown className="dropdown-icon" />
                </div>
                {showCategories && (
                  <div className="category-dropdown mobile-dropdown">
                    <ul className="category-list">
                      {loading ? (
                        <li className="category-item">Loading categories...</li>
                      ) : categories.length > 0 ? (
                        categories.map(category => (
                          <li key={category.id} className="category-items">
                            <Link 
                              to={`/shop?category=${category.id}`} 
                              className="category-link"
                              onClick={closeMobileMenu}
                            >
                              {category.name.toUpperCase()}
                            </Link>
                          </li>
                        ))
                      ) : (
                        <li className="category-item">No categories found</li>
                      )}
                    </ul>
                  </div>
                )}
              </li>
              <li className="nav-item">
                <Link 
                  to="/about" 
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  ABOUT US
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  to="/contact" 
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  CONTACT US
                </Link>
              </li>
              {currentUser && (
                <li className="nav-item">
                  <Link 
                    to="/my-account" 
                    className="nav-link"
                    onClick={closeMobileMenu}
                  >
                    MY ACCOUNT
                  </Link>
                </li>
              )}
              
              {/* Mobile-specific user and cart sections */}
              <li className="nav-item mobile-user-cart">
                <div className="mobile-user-actions">
                  {currentUser ? (
                    <button 
                      className="action-link logout-btn"
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt className="action-icon" />
                      LOGOUT
                    </button>
                  ) : (
                    <Link 
                      to="/login" 
                      className="action-link"
                      onClick={closeMobileMenu}
                    >
                      <FaUser className="action-icon" />
                      LOGIN / REGISTER
                    </Link>
                  )}
                </div>
                <Link to='/cart' onClick={closeMobileMenu}>
                  <div className="cart-info">
                    <span className="cart-count">{cartItemCount}</span>
                    <span className="divider">/</span>
                    <span className="cart-total">€{cartTotal}</span>
                    <FaShoppingCart className="cart-icon" />
                  </div>
                </Link>
              </li>
            </ul>
          </nav>

          <div className="user-cart-section desktop-user-cart">
            <div className="user-actions">
              {currentUser ? (
                <div className="user-menu">
                  <Link to="/my-account" className="user-greeting">
                    <FaUser className="action-icon" />
                    {currentUser.name ? `Hi, ${currentUser.name.split(' ')[0]}` : 'My Account'}
                  </Link>
                  <button onClick={handleLogout} className="logout-btn">
                    <FaSignOutAlt className="action-icon" />
                    LOGOUT
                  </button>
                </div>
              ) : (
                <Link to="/login" className="action-link">
                  <FaUser className="action-icon" />
                  LOGIN / REGISTER
                </Link>
              )}
            </div>
            <Link to='/cart' className="cart-link">
              <div className="cart-info">
                <span className="cart-count">{cartItemCount}</span>
                <span className="divider">/</span>
                <span className="cart-total">€{cartTotal}</span>
                <FaShoppingCart className="cart-icon" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;